/**
 * Payments Manager — standalone page (/payments).
 *
 * Two jobs for a selected group-period:
 *  1. Charges (admin): the named charges every student in the gp inherits. Stored as
 *     relationships (source_slug='payments'); title / amount / due date / requirements live in
 *     metadata. API: /api/payments/schedule.
 *  2. Roster: every active student × charge, with derived payment status. A charge with no
 *     transactions defaults to Unpaid. Click a cell to record payments. API:
 *     /api/payments/roster and /api/payments/transactions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PaymentChargeEditor from '../components/payments/PaymentChargeEditor';
import RosterPaymentCell, { RosterActionLegend } from '../components/payments/RosterPaymentCell';
import './PaymentsManager.css';
import ProfileFormSheet from '../components/modals/ProfileFormSheet';
import CornerNotice from '../components/notifications/CornerNotice';
import {
  formatPaymentAmount,
  formatMonthKeyLabel,
  formatMonthKeyShort,
  dateTimeLocalToStorage,
  deriveStatusClient
} from '../constants/paymentStatuses';

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DuplicateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const EMPTY_SCHEDULE_FORM = {
  title: '',
  amount: '',
  currency: 'USD',
  amount_required: false,
  required_by_date: ''
};

function cellPatchFromDerived(derived, existingCell = {}) {
  return {
    status: derived.status,
    net_paid: derived.net_paid,
    amount_due: derived.amount_due ?? existingCell.amount_due,
    tracking_mode: derived.tracking_mode ?? existingCell.tracking_mode,
    transaction_count:
      derived.transactions?.length ?? derived.transaction_count ?? existingCell.transaction_count
  };
}

const PaymentsManager = () => {
  const [groupsPeriods, setGroupsPeriods] = useState([]);
  const [scheduledGps, setScheduledGps] = useState([]);
  const [selectedGpId, setSelectedGpId] = useState('');
  const [search, setSearch] = useState('');
  const [hierarchyMeta, setHierarchyMeta] = useState({});
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  const [schedule, setSchedule] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null); // { tone:'success'|'error', title, text }

  const [scheduleModal, setScheduleModal] = useState(null); // { mode:'add'|'edit', form, relId, chargeCode }
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [cellModal, setCellModal] = useState(null); // { student, cell }
  const [savingCell, setSavingCell] = useState(false);
  const [quickSettingKey, setQuickSettingKey] = useState(null); // `${ugpId}:${requirementId}`

  // Map each group-period to its organization label via the org→nested-groups hierarchy.
  const buildHierarchyMeta = useCallback(async (allGpRows) => {
    try {
      const roots = (allGpRows || []).filter((gp) => {
        const t = String(gp.group_type || '').toLowerCase();
        return t === 'organization' || t === 'system';
      });
      const meta = {};
      roots.forEach((root) => {
        meta[String(root.id)] = { organization: root.group_name || 'Organization' };
      });
      const results = await Promise.all(
        roots.map(async (root) => {
          try {
            const r = await fetch(
              `/api/relationships/nested-groups?superparent_groups_periods_id=${encodeURIComponent(root.id)}`
            );
            const d = await r.json();
            return { org: root.group_name || 'Organization', groups: d.success ? d.data || [] : [] };
          } catch {
            return { org: root.group_name || 'Organization', groups: [] };
          }
        })
      );
      results.forEach(({ org, groups }) => {
        (groups || []).forEach((g) => {
          (g.periods || []).forEach((p) => {
            meta[String(p.id)] = { organization: org };
          });
        });
      });
      setHierarchyMeta(meta);
    } catch {
      setHierarchyMeta({});
    }
  }, []);

  const loadGroupsPeriods = useCallback(async () => {
    try {
      const [allRes, schedRes] = await Promise.all([
        fetch('/api/groups-periods'),
        fetch('/api/payments/groups-periods')
      ]);
      const [allData, schedData] = await Promise.all([allRes.json(), schedRes.json()]);
      if (allData.success) {
        setGroupsPeriods(allData.data || []);
        buildHierarchyMeta(allData.data || []);
      }
      if (schedData.success) setScheduledGps(schedData.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load group-periods');
    }
  }, [buildHierarchyMeta]);

  useEffect(() => {
    loadGroupsPeriods();
  }, [loadGroupsPeriods]);

  const loadRoster = useCallback(async (gpId, { silent = false } = {}) => {
    if (!gpId) {
      setSchedule([]);
      setStudents([]);
      return;
    }
    if (!silent) setLoadingRoster(true);
    setError('');
    try {
      const res = await fetch(`/api/payments/roster?groups_periods_id=${encodeURIComponent(gpId)}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load roster');
      setSchedule(data.data?.schedule || []);
      setStudents(data.data?.students || []);
    } catch (err) {
      setError(err.message || 'Failed to load roster');
      if (!silent) {
        setSchedule([]);
        setStudents([]);
      }
    } finally {
      if (!silent) setLoadingRoster(false);
    }
  }, []);

  const applyCellUpdate = useCallback((ugpId, requirementId, derived) => {
    const patch = cellPatchFromDerived(derived);
    setStudents((prev) =>
      prev.map((student) => {
        if (String(student.ugp_id) !== String(ugpId)) return student;
        return {
          ...student,
          cells: student.cells.map((cell) =>
            String(cell.requirement_id) === String(requirementId) ? { ...cell, ...patch } : cell
          )
        };
      })
    );
  }, []);

  useEffect(() => {
    loadRoster(selectedGpId);
  }, [selectedGpId, loadRoster]);

  const selectedGp = useMemo(
    () => groupsPeriods.find((g) => String(g.id) === String(selectedGpId)) || null,
    [groupsPeriods, selectedGpId]
  );

  const filteredGps = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...groupsPeriods].sort((a, b) =>
      `${a.group_name || ''} ${a.period_title || ''}`.localeCompare(`${b.group_name || ''} ${b.period_title || ''}`)
    );
    if (!q) return list;
    return list.filter((g) =>
      `${g.group_name || ''} ${g.period_title || ''} ${g.title || ''}`.toLowerCase().includes(q)
    );
  }, [groupsPeriods, search]);

  // Schedule/student counts keyed by gp id, for badges in the selector.
  const scheduledById = useMemo(() => {
    const map = new Map();
    (scheduledGps || []).forEach((gp) => map.set(String(gp.id), gp));
    return map;
  }, [scheduledGps]);

  const orgLabelFor = useCallback(
    (gp) => {
      const meta = hierarchyMeta[String(gp.id)];
      if (meta?.organization) return meta.organization;
      if (String(gp.group_type || '').toLowerCase() === 'organization') return gp.group_name || 'Organization';
      return 'Other / System';
    },
    [hierarchyMeta]
  );

  // Build an organization → group → group-period tree from the filtered list.
  const orgTree = useMemo(() => {
    const orgs = new Map();
    filteredGps.forEach((gp) => {
      const t = String(gp.group_type || '').toLowerCase();
      if (t === 'organization' || t === 'system') return; // container nodes aren't selectable
      const orgName = orgLabelFor(gp);
      const groupName = gp.group_name || gp.title || 'Ungrouped';
      if (!orgs.has(orgName)) orgs.set(orgName, new Map());
      const groupMap = orgs.get(orgName);
      if (!groupMap.has(groupName)) groupMap.set(groupName, []);
      groupMap.get(groupName).push(gp);
    });

    return Array.from(orgs.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([organization, groupMap]) => {
        const groups = Array.from(groupMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([groupName, items]) => ({
            groupName,
            items: items.sort((a, b) => `${a.period_title || ''}`.localeCompare(`${b.period_title || ''}`)),
            scheduledCount: items.filter((g) => scheduledById.has(String(g.id))).length
          }));
        const periodCount = groups.reduce((n, g) => n + g.items.length, 0);
        const scheduledCount = groups.reduce((n, g) => n + g.scheduledCount, 0);
        return { organization, groups, periodCount, scheduledCount };
      });
  }, [filteredGps, orgLabelFor, scheduledById]);

  const searching = search.trim().length > 0;
  const toggleOrg = (org) => setExpandedOrgs((p) => ({ ...p, [org]: !p[org] }));
  const toggleGroup = (key) => setExpandedGroups((p) => ({ ...p, [key]: !p[key] }));

  /* ----------------------------- schedule ----------------------------- */

  const openAddSchedule = () => setScheduleModal({ mode: 'add', form: { ...EMPTY_SCHEDULE_FORM }, relId: null });

  const openEditSchedule = (item) =>
    setScheduleModal({
      mode: 'edit',
      relId: item.requirement_id,
      chargeCode: item.charge_code || null,
      form: {
        ...EMPTY_SCHEDULE_FORM,
        title: item.label || (item.month_key ? formatMonthKeyLabel(item.month_key) : ''),
        amount: item.amount != null ? String(item.amount) : '',
        currency: item.currency || 'USD',
        amount_required: item.amount_required === true,
        required_by_date: item.required_by_date || ''
      }
    });

  const handleSaveSchedule = async () => {
    if (!scheduleModal) return;
    const { mode, form, relId } = scheduleModal;
    const title = String(form.title || '').trim();
    if (!title) {
      setNotice({ tone: 'error', title: 'Title required', text: 'Give this charge a title (e.g. “Tuition”).' });
      return;
    }

    const commonBody = {
      groups_periods_id: selectedGpId,
      amount: form.amount,
      minimum_payment: '',
      currency: form.currency,
      amount_required: form.amount_required,
      required: true
    };

    setSavingSchedule(true);
    setError('');
    try {
      if (mode === 'edit') {
        const res = await fetch(`/api/payments/schedule/${relId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...commonBody, label: title, required_by_date: form.required_by_date })
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      } else {
        const res = await fetch('/api/payments/schedule', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...commonBody, label: title, required_by_date: form.required_by_date })
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      }
      setScheduleModal(null);
      setNotice({
        tone: 'success',
        title: mode === 'edit' ? 'Charge updated' : 'Charge added',
        text: title
      });
      await loadRoster(selectedGpId, { silent: true });
      await loadGroupsPeriods();
    } catch (err) {
      setNotice({ tone: 'error', title: 'Save failed', text: err.message || 'Save failed' });
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (item) => {
    if (!window.confirm(`Remove “${item.label || item.month_key}”? Existing student records are kept.`)) {
      return;
    }
    try {
      const res = await fetch(`/api/payments/schedule/${item.requirement_id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Delete failed');
      setNotice({ tone: 'success', title: 'Charge removed', text: item.label || '' });
      await loadRoster(selectedGpId, { silent: true });
      await loadGroupsPeriods();
    } catch (err) {
      setNotice({ tone: 'error', title: 'Delete failed', text: err.message || 'Delete failed' });
    }
  };

  const handleDuplicateSchedule = async (item) => {
    const title = item.label || (item.month_key ? formatMonthKeyLabel(item.month_key) : 'Charge');
    setDuplicatingId(item.requirement_id);
    setError('');
    try {
      const res = await fetch('/api/payments/schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groups_periods_id: selectedGpId,
          label: title,
          amount: item.amount != null ? String(item.amount) : '',
          currency: item.currency || 'USD',
          amount_required: item.amount_required === true,
          required: true,
          minimum_payment: '',
          required_by_date: item.required_by_date || ''
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Duplicate failed');
      const code = data.data?.charge_code;
      setNotice({
        tone: 'success',
        title: 'Charge duplicated',
        text: code ? `${title} (${code})` : title
      });
      await loadRoster(selectedGpId, { silent: true });
      await loadGroupsPeriods();
    } catch (err) {
      setNotice({ tone: 'error', title: 'Duplicate failed', text: err.message || 'Duplicate failed' });
    } finally {
      setDuplicatingId(null);
    }
  };

  /* ------------------------------ cells ------------------------------ */

  const fetchCellTxns = useCallback(async (ugpId, requirementId) => {
    const res = await fetch(
      `/api/payments/transactions?user_groups_periods_id=${encodeURIComponent(ugpId)}&requirement_id=${encodeURIComponent(requirementId)}`
    );
    const data = await res.json();
    return data.success ? data.data || [] : [];
  }, []);

  const openCell = async (student, cell) => {
    setCellModal({ student, cell, txns: [], loading: true });
    try {
      const txns = await fetchCellTxns(student.ugp_id, cell.requirement_id);
      setCellModal((m) => (m ? { ...m, txns, loading: false } : m));
    } catch {
      setCellModal((m) => (m ? { ...m, loading: false } : m));
    }
  };

  const refreshCell = useCallback(
    async (student, cell) => {
      try {
        const txns = await fetchCellTxns(student.ugp_id, cell.requirement_id);
        const derived = deriveStatusClient(txns, {
          amount: cell.amount_due ?? cell.amount,
          amount_due: cell.amount_due,
          tracking_mode: cell.tracking_mode
        });
        const patch = { ...derived, transactions: txns };
        applyCellUpdate(student.ugp_id, cell.requirement_id, patch);
        setCellModal((m) => {
          if (!m || String(m.student?.ugp_id) !== String(student.ugp_id)) return m;
          return {
            ...m,
            txns,
            cell: { ...m.cell, ...cellPatchFromDerived(derived, m.cell) }
          };
        });
      } catch {
        /* ignore */
      }
    },
    [fetchCellTxns, applyCellUpdate]
  );

  const runQuickSet = async (student, cell, status) => {
    const key = `${student.ugp_id}:${cell.requirement_id}`;
    setQuickSettingKey(key);
    setSavingCell(true);
    setError('');
    try {
      const res = await fetch('/api/payments/quick-set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_groups_periods_id: student.ugp_id,
          requirement_id: cell.requirement_id,
          status
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      applyCellUpdate(student.ugp_id, cell.requirement_id, data.data);
      if (
        cellModal &&
        String(cellModal.student?.ugp_id) === String(student.ugp_id) &&
        String(cellModal.cell?.requirement_id) === String(cell.requirement_id)
      ) {
        setCellModal((m) =>
          m
            ? {
                ...m,
                txns: data.data.transactions || m.txns,
                cell: { ...m.cell, ...cellPatchFromDerived(data.data, m.cell) }
              }
            : m
        );
      }
    } catch (err) {
      setError(err.message || 'Save failed');
      setNotice({ tone: 'error', title: 'Quick set failed', text: err.message || 'Save failed' });
    } finally {
      setSavingCell(false);
      setQuickSettingKey(null);
    }
  };

  const handleAddCellTxn = async (draft) => {
    if (!cellModal) return false;
    const { student, cell } = cellModal;
    setSavingCell(true);
    setError('');
    try {
      const res = await fetch('/api/payments/transactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_groups_periods_id: student.ugp_id,
          requirement_id: cell.requirement_id,
          txn_type: draft.txn_type,
          amount: draft.amount === '' || draft.amount == null ? null : draft.amount,
          is_partial: draft.txn_type === 'payment' ? draft.is_partial === true : false,
          reason: draft.reason || null,
          method: draft.method || null,
          occurred_on: dateTimeLocalToStorage(draft.occurred_on),
          notes: draft.notes || null,
          currency: cell.currency
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      await refreshCell(student, cell);
      return true;
    } catch (err) {
      setError(err.message || 'Save failed');
      return false;
    } finally {
      setSavingCell(false);
    }
  };

  const handleDeleteCellTxn = async (txnId) => {
    if (!cellModal) return;
    const { student, cell } = cellModal;
    setSavingCell(true);
    setError('');
    try {
      const res = await fetch(`/api/payments/transactions/${txnId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Delete failed');
      await refreshCell(student, cell);
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setSavingCell(false);
    }
  };

  const gpLabel = (gp) => `${gp.group_name || gp.title || 'Group'} — ${gp.period_title || 'Period'}`;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Payments</h1>
          <div style={styles.subtitle}>
            Define the charges for a group-period (every student inherits them) and track each student&apos;s
            status. A charge with no recorded transactions shows as Unpaid.
          </div>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <section style={styles.panelCompact}>
        <div style={styles.selectorHead}>
          <div style={styles.panelTitle}>Group-period</div>
          {selectedGp && (
            <div style={styles.selectedPill} title={gpLabel(selectedGp)}>
              {gpLabel(selectedGp)}
            </div>
          )}
        </div>

        <div style={styles.searchWrap}>
          <SearchIcon />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by group or period…"
            style={styles.searchInput}
          />
          {search && (
            <button type="button" style={styles.searchClear} onClick={() => setSearch('')} aria-label="Clear search">
              ×
            </button>
          )}
        </div>

        <div style={styles.selectorList}>
          {orgTree.length === 0 ? (
            <div style={styles.selectorEmpty}>
              {searching ? `No group-periods match “${search}”.` : 'No group-periods available.'}
            </div>
          ) : (
            orgTree.map((org) => {
              const orgOpen = searching || !!expandedOrgs[org.organization];
              return (
                <div key={org.organization} style={styles.orgBlock}>
                  <button type="button" style={styles.orgHead} onClick={() => toggleOrg(org.organization)}>
                    <span style={{ ...styles.caret, transform: orgOpen ? 'none' : 'rotate(-90deg)' }}>▾</span>
                    <span style={styles.orgName}>{org.organization}</span>
                    <span style={styles.orgMeta}>
                      {org.periodCount}
                      {org.scheduledCount > 0 ? ` · ${org.scheduledCount} active` : ''}
                    </span>
                  </button>
                  {orgOpen &&
                    org.groups.map((group) => {
                      const gKey = `${org.organization}::${group.groupName}`;
                      const hasSelected = group.items.some((g) => String(g.id) === String(selectedGpId));
                      const gOpen = searching || hasSelected || !!expandedGroups[gKey];
                      return (
                        <div key={gKey}>
                          <button type="button" style={styles.groupHead} onClick={() => toggleGroup(gKey)}>
                            <span style={{ ...styles.caretSm, transform: gOpen ? 'none' : 'rotate(-90deg)' }}>▾</span>
                            <span style={styles.groupName}>{group.groupName}</span>
                            <span style={styles.groupMeta}>
                              {group.items.length}
                              {group.scheduledCount > 0 ? ` · ${group.scheduledCount}●` : ''}
                            </span>
                          </button>
                          {gOpen &&
                            group.items.map((gp) => {
                              const sched = scheduledById.get(String(gp.id));
                              const active = String(gp.id) === String(selectedGpId);
                              return (
                                <button
                                  key={gp.id}
                                  type="button"
                                  onClick={() => setSelectedGpId(gp.id)}
                                  style={{ ...styles.selectorItem, ...(active ? styles.selectorItemActive : {}) }}
                                >
                                  <span style={styles.selectorItemTitle}>{gp.period_title || gp.title || 'Period'}</span>
                                  {sched && (
                                    <span style={styles.selectorBadge} title={`${sched.schedule_count} charge(s), ${sched.student_count} student(s)`}>
                                      {sched.schedule_count}c · {sched.student_count}s
                                    </span>
                                  )}
                                  {active && <span style={styles.selectorCheck}>✓</span>}
                                </button>
                              );
                            })}
                        </div>
                      );
                    })}
                </div>
              );
            })
          )}
        </div>
      </section>

      {selectedGpId && (
        <>
          <section style={styles.panelCompact}>
            <div style={styles.panelHeadCompact}>
              <div style={styles.panelTitle}>Charges</div>
              <div style={styles.panelHintCompact}>
                {students.length} student{students.length === 1 ? '' : 's'}
                {selectedGp ? ` · ${gpLabel(selectedGp)}` : ''}
              </div>
            </div>

            <div style={styles.chargeGrid}>
              <button type="button" style={styles.addCard} onClick={openAddSchedule} title="Add charge">
                <PlusIcon />
              </button>
              {schedule.map((item) => (
                <div key={item.requirement_id} style={styles.chargeCard}>
                  <div style={styles.chargeCardActions}>
                    <button
                      type="button"
                      style={styles.iconBtn}
                      onClick={() => openEditSchedule(item)}
                      title="Edit charge"
                      aria-label="Edit charge"
                    >
                      <PencilIcon />
                    </button>
                    <button
                      type="button"
                      style={styles.iconBtn}
                      onClick={() => handleDuplicateSchedule(item)}
                      disabled={duplicatingId === item.requirement_id}
                      title="Duplicate charge"
                      aria-label="Duplicate charge"
                    >
                      <DuplicateIcon />
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.iconBtn, ...styles.iconBtnDanger }}
                      onClick={() => handleDeleteSchedule(item)}
                      title="Remove charge"
                      aria-label="Remove charge"
                    >
                      <XIcon />
                    </button>
                  </div>
                  {item.charge_code && <div style={styles.chargeCardCode}>{item.charge_code}</div>}
                  <div style={styles.chargeCardName}>{item.label || formatMonthKeyLabel(item.month_key)}</div>
                  <div style={item.amount != null ? styles.chargeCardAmount : styles.chargeCardAmountEmpty}>
                    {item.amount != null ? formatPaymentAmount(item.amount, item.currency) : 'No amount specified'}
                  </div>
                  <div style={styles.chargeCardMeta}>
                    {item.required_by_date ? `due by ${item.required_by_date}` : ''}
                  </div>
                  {!item.required && <span style={styles.optionalTag}>Optional</span>}
                </div>
              ))}
            </div>
          </section>

          <section style={styles.panel}>
            <div style={styles.panelHead}>
              <div>
                <div style={styles.panelTitle}>Roster</div>
                <div style={styles.panelHint}>
                  Status on top opens the editor (waiver, refunds, notes). Icons below quick-set paid,
                  failed, insufficient funds, or partial — no page reload.
                </div>
              </div>
            </div>

            {!loadingRoster && students.length > 0 && schedule.length > 0 && (
              <div style={styles.legendWrap}>
                <RosterActionLegend />
              </div>
            )}

            {loadingRoster ? (
              <div style={styles.statePad}>Loading roster…</div>
            ) : students.length === 0 ? (
              <div style={styles.emptyState}>No active students enrolled in this group-period.</div>
            ) : schedule.length === 0 ? (
              <div style={styles.emptyState}>Add charges above to track payments.</div>
            ) : (
              <div className="payments-roster-scroll" style={styles.tableScroll}>
                <table style={styles.table} className="payments-roster-table">
                  <thead>
                    <tr>
                      <th className="payments-roster-th payments-roster-th--corner" style={styles.th}>Student</th>
                      {schedule.map((s) => (
                        <th
                          key={s.requirement_id}
                          className="payments-roster-th"
                          style={styles.thCol}
                          title={s.charge_code ? `${s.charge_code} · ${s.label || s.month_key}` : s.label || s.month_key}
                        >
                          <div style={styles.thColTitle}>{s.label || (s.month_key ? formatMonthKeyShort(s.month_key) : 'Charge')}</div>
                          {s.charge_code && <div style={styles.thColCode}>{s.charge_code}</div>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student.ugp_id}>
                        <td className="payments-roster-td-student" style={styles.tdName}>
                          <div style={styles.studentName}>{student.user_name || 'Unnamed'}</div>
                          {student.email && <div style={styles.studentEmail}>{student.email}</div>}
                        </td>
                        {student.cells.map((cell) => {
                          const cellKey = `${student.ugp_id}:${cell.requirement_id}`;
                          return (
                            <td key={cell.requirement_id} style={styles.td}>
                              <RosterPaymentCell
                                cell={cell}
                                busy={quickSettingKey === cellKey}
                                onOpen={() => openCell(student, cell)}
                                onQuickSet={(status) => runQuickSet(student, cell, status)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      <ProfileFormSheet
        open={!!scheduleModal}
        onClose={() => !savingSchedule && setScheduleModal(null)}
        closeDisabled={savingSchedule}
        title={scheduleModal?.mode === 'edit' ? 'Edit charge' : 'Add charge'}
        footer={
          <>
            <button type="button" style={styles.ghostButton} onClick={() => setScheduleModal(null)} disabled={savingSchedule}>
              Cancel
            </button>
            <button type="button" style={styles.saveButton} onClick={handleSaveSchedule} disabled={savingSchedule}>
              {savingSchedule ? 'Saving…' : scheduleModal?.mode === 'edit' ? 'Save' : 'Add'}
            </button>
          </>
        }
      >
        {scheduleModal && (
          <>
            <div style={styles.fieldTitle}>
              <label style={styles.labelTitle}>Title</label>
              <input
                type="text"
                value={scheduleModal.form.title}
                onChange={(e) => setScheduleModal((m) => ({ ...m, form: { ...m.form, title: e.target.value } }))}
                placeholder="e.g. Tuition, Registration fee"
                style={styles.inputTitle}
                autoFocus
              />
            </div>

            {scheduleModal.mode === 'edit' && scheduleModal.chargeCode && (
              <div style={styles.chargeIdRow}>
                <span style={styles.chargeIdLabel}>Charge ID</span>
                <span style={styles.chargeIdValue}>{scheduleModal.chargeCode}</span>
              </div>
            )}

            <div style={styles.formDivider} />
            <div style={styles.optionalLabel}>Optional</div>

            <div style={styles.fieldRowCompact}>
              <div style={{ ...styles.fieldCompact, flex: 1 }}>
                <label style={styles.labelOptional}>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={scheduleModal.form.amount}
                  onChange={(e) => setScheduleModal((m) => ({ ...m, form: { ...m.form, amount: e.target.value } }))}
                  placeholder="0.00"
                  style={styles.inputCompact}
                />
              </div>
              <div style={{ ...styles.fieldCompact, width: 72 }}>
                <label style={styles.labelOptional}>Currency</label>
                <input type="text" value={scheduleModal.form.currency} disabled style={styles.inputCompactDisabled} />
              </div>
            </div>

            <div style={styles.fieldCompact}>
              <label style={styles.labelOptional}>Payment due by</label>
              <input
                type="date"
                value={scheduleModal.form.required_by_date}
                onChange={(e) =>
                  setScheduleModal((m) => ({ ...m, form: { ...m.form, required_by_date: e.target.value } }))
                }
                style={styles.inputCompact}
              />
            </div>

            <label style={styles.checkRowCompact}>
              <input
                type="checkbox"
                checked={scheduleModal.form.amount_required}
                onChange={(e) =>
                  setScheduleModal((m) => ({ ...m, form: { ...m.form, amount_required: e.target.checked } }))
                }
              />
              <span style={styles.checkTitleCompact}>Require Payment Amount Entry</span>
            </label>
          </>
        )}
      </ProfileFormSheet>

      <PaymentChargeEditor
        open={!!cellModal}
        onClose={() => !savingCell && setCellModal(null)}
        saving={savingCell}
        loading={cellModal?.loading}
        title={cellModal?.student?.user_name || 'Student'}
        subtitle={
          cellModal?.cell?.charge_code
            ? cellModal.cell.charge_code
            : cellModal?.cell?.month_key
              ? formatMonthKeyLabel(cellModal.cell.month_key)
              : undefined
        }
        charge={
          cellModal
            ? {
                ...cellModal.cell,
                transactions: cellModal.txns,
                requirement_id: cellModal.cell.requirement_id
              }
            : {}
        }
        onAddTransaction={handleAddCellTxn}
        onDeleteTransaction={handleDeleteCellTxn}
      />

      <CornerNotice
        key={notice ? `${notice.title}|${notice.text}` : 'none'}
        isOpen={!!notice}
        onClose={() => setNotice(null)}
        title={notice?.title}
        text={notice?.text}
        position="bottom-right"
        containerColor={notice?.tone === 'error' ? '#fef2f2' : '#ecfdf5'}
        textColor={notice?.tone === 'error' ? '#991b1b' : '#065f46'}
      />
    </div>
  );
};

const colors = {
  text: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  brand: '#334155'
};

const fontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif';

const styles = {
  container: {
    maxWidth: 1200,
    margin: '20px auto',
    padding: 16,
    fontFamily: fontStack,
    color: colors.text
  },
  header: { marginBottom: 12 },
  title: { fontSize: '1.15rem', fontWeight: 600, margin: 0 },
  subtitle: { fontSize: '0.8rem', color: colors.textSecondary, marginTop: 4, maxWidth: 720, lineHeight: 1.45 },
  error: {
    padding: '8px 12px',
    background: '#fef2f2',
    color: '#991b1b',
    border: '1px solid #fecaca',
    borderRadius: 6,
    marginBottom: 10,
    fontSize: '0.82rem'
  },
  panel: {
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
    background: colors.surface
  },
  panelCompact: {
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 10,
    background: colors.surface
  },
  panelHeadCompact: { marginBottom: 8 },
  panelHintCompact: { fontSize: '0.7rem', color: colors.textMuted, marginTop: 2 },
  fieldTitle: { marginBottom: 4 },
  labelTitle: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: colors.text, marginBottom: 6 },
  inputTitle: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: '0.95rem',
    fontWeight: 500,
    fontFamily: fontStack,
    background: colors.surface,
    color: colors.text
  },
  chargeIdRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8
  },
  chargeIdLabel: { fontSize: '0.68rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' },
  chargeIdValue: {
    fontSize: '0.72rem',
    fontWeight: 600,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    color: colors.textSecondary
  },
  formDivider: { border: 'none', borderTop: `1px solid ${colors.border}`, margin: '12px 0 10px' },
  optionalLabel: {
    fontSize: '0.62rem',
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 8
  },
  fieldCompact: { marginBottom: 8 },
  fieldRowCompact: { display: 'flex', gap: 8, alignItems: 'flex-end' },
  labelOptional: { display: 'block', fontSize: '0.68rem', fontWeight: 500, color: colors.textMuted, marginBottom: 3 },
  inputCompact: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '6px 9px',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: '0.8rem',
    fontFamily: fontStack,
    background: colors.surfaceAlt,
    color: colors.text
  },
  inputCompactDisabled: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '6px 9px',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: '0.8rem',
    fontFamily: fontStack,
    background: '#f1f5f9',
    color: colors.textMuted,
    cursor: 'not-allowed'
  },
  checkRowCompact: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    marginBottom: 0,
    cursor: 'pointer'
  },
  checkTitleCompact: { fontSize: '0.78rem', color: colors.textSecondary },
  label: { display: 'block', fontSize: '0.72rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: 7,
    fontSize: '0.85rem',
    fontFamily: fontStack,
    background: colors.surfaceAlt
  },
  inputDisabled: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: 7,
    fontSize: '0.85rem',
    fontFamily: fontStack,
    background: '#f1f5f9',
    color: colors.textMuted,
    cursor: 'not-allowed'
  },
  selectorHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
    flexWrap: 'wrap'
  },
  selectedPill: {
    maxWidth: 280,
    padding: '2px 8px',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    background: colors.surfaceAlt,
    color: colors.textSecondary,
    fontSize: '0.68rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '6px 9px',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    background: colors.surfaceAlt
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '0.82rem',
    fontFamily: fontStack,
    color: colors.text
  },
  searchClear: {
    border: 'none',
    background: 'transparent',
    color: colors.textMuted,
    fontSize: '1.05rem',
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0
  },
  selectorList: {
    marginTop: 6,
    maxHeight: 240,
    overflowY: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    background: colors.surface
  },
  selectorEmpty: { padding: 12, textAlign: 'center', fontSize: '0.76rem', color: colors.textMuted },
  caret: { fontSize: '0.6rem', color: colors.textMuted, transition: 'transform 0.12s ease', display: 'inline-block', width: 10 },
  caretSm: { fontSize: '0.55rem', color: colors.textMuted, transition: 'transform 0.12s ease', display: 'inline-block', width: 9 },
  orgBlock: { borderBottom: `1px solid ${colors.border}` },
  orgHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    width: '100%',
    padding: '5px 10px',
    border: 'none',
    background: colors.surfaceAlt,
    cursor: 'pointer',
    fontFamily: fontStack,
    textAlign: 'left'
  },
  orgName: { fontWeight: 600, fontSize: '0.72rem', color: colors.textSecondary, letterSpacing: '0.02em' },
  orgMeta: { marginLeft: 'auto', fontSize: '0.65rem', color: colors.textMuted },
  groupHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    width: '100%',
    padding: '4px 10px 4px 20px',
    border: 'none',
    borderTop: `1px solid ${colors.border}`,
    background: colors.surface,
    cursor: 'pointer',
    fontFamily: fontStack,
    textAlign: 'left'
  },
  groupName: { fontWeight: 500, fontSize: '0.74rem', color: colors.text },
  groupMeta: { marginLeft: 'auto', fontSize: '0.65rem', color: colors.textMuted },
  selectorItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '4px 10px 4px 34px',
    border: 'none',
    borderTop: `1px solid ${colors.border}`,
    background: colors.surface,
    cursor: 'pointer',
    fontFamily: fontStack,
    textAlign: 'left'
  },
  selectorItemActive: { background: colors.surfaceAlt, boxShadow: `inset 2px 0 0 ${colors.textSecondary}` },
  selectorItemTitle: { flex: 1, minWidth: 0, fontSize: '0.76rem', fontWeight: 500, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  selectorBadge: {
    fontSize: '0.62rem',
    fontWeight: 500,
    color: colors.textMuted,
    background: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: 4,
    padding: '0 5px',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  selectorCheck: { color: colors.textSecondary, fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 },
  panelHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10, flexWrap: 'wrap' },
  panelTitle: { fontSize: '0.84rem', fontWeight: 600, color: colors.text },
  panelHint: { fontSize: '0.74rem', color: colors.textMuted, marginTop: 2 },
  ghostButton: {
    padding: '5px 11px',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: '0.76rem',
    color: colors.textSecondary,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  saveButton: {
    padding: '5px 11px',
    background: colors.text,
    border: `1px solid ${colors.text}`,
    color: '#fff',
    borderRadius: 6,
    fontSize: '0.76rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  primaryButton: {
    padding: '6px 12px',
    background: colors.text,
    border: `1px solid ${colors.text}`,
    color: '#fff',
    borderRadius: 7,
    fontSize: '0.78rem',
    cursor: 'pointer',
    fontFamily: fontStack
  },
  emptyState: {
    padding: 14,
    background: colors.surfaceAlt,
    border: `1px dashed ${colors.border}`,
    borderRadius: 8,
    fontSize: '0.8rem',
    color: colors.textMuted,
    textAlign: 'center'
  },
  statePad: { padding: 20, textAlign: 'center', color: colors.textSecondary, fontSize: '0.82rem' },
  chargeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 6
  },
  chargeCard: {
    position: 'relative',
    border: `1px solid ${colors.border}`,
    borderRadius: 7,
    padding: '8px 10px',
    background: colors.surface,
    minHeight: 64
  },
  chargeCardActions: { position: 'absolute', top: 4, right: 4, display: 'flex', gap: 1 },
  iconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    padding: 0,
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    color: colors.textMuted,
    cursor: 'pointer'
  },
  iconBtnDanger: { color: '#b91c1c' },
  chargeCardCode: {
    fontSize: '0.62rem',
    fontWeight: 600,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    color: colors.textMuted,
    letterSpacing: '0.03em',
    marginBottom: 1
  },
  chargeCardName: { fontWeight: 600, fontSize: '0.76rem', color: colors.text, paddingRight: 70, lineHeight: 1.25 },
  chargeCardAmount: { marginTop: 3, fontWeight: 600, fontSize: '0.8rem', color: colors.textSecondary },
  chargeCardAmountEmpty: {
    marginTop: 3,
    fontWeight: 400,
    fontSize: '0.65rem',
    color: colors.textMuted,
    fontStyle: 'italic'
  },
  chargeCardMeta: { marginTop: 2, fontSize: '0.65rem', color: colors.textMuted, minHeight: 12 },
  optionalTag: {
    display: 'inline-block',
    marginTop: 4,
    fontSize: '0.6rem',
    fontWeight: 500,
    color: colors.textMuted,
    background: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: 3,
    padding: '0 5px'
  },
  addCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 64,
    border: `1px dashed ${colors.border}`,
    borderRadius: 7,
    background: colors.surfaceAlt,
    color: colors.textMuted,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  legendWrap: { marginBottom: 10 },
  tableScroll: { border: `1px solid ${colors.border}`, borderRadius: 8 },
  table: { borderCollapse: 'separate', borderSpacing: 0, width: '100%', fontSize: '0.78rem' },
  th: {
    borderBottom: `1px solid ${colors.border}`,
    padding: '8px 10px',
    textAlign: 'left',
    fontWeight: 600,
    color: colors.textSecondary,
    whiteSpace: 'nowrap'
  },
  thCol: {
    borderBottom: `1px solid ${colors.border}`,
    padding: '6px 8px',
    textAlign: 'center',
    fontWeight: 600,
    color: colors.textSecondary,
    verticalAlign: 'bottom',
    minWidth: 112
  },
  thColTitle: { fontSize: '0.74rem', lineHeight: 1.2, whiteSpace: 'nowrap' },
  thColCode: {
    marginTop: 2,
    fontSize: '0.6rem',
    fontWeight: 600,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    color: colors.textMuted,
    letterSpacing: '0.02em'
  },
  tdName: {
    padding: '8px 10px',
    borderBottom: `1px solid ${colors.border}`,
    minWidth: 180
  },
  studentName: { fontWeight: 600, fontSize: '0.82rem' },
  studentEmail: { fontSize: '0.7rem', color: colors.textMuted },
  td: { padding: '4px 6px', borderBottom: `1px solid ${colors.border}`, textAlign: 'center' },
  statusCell: {
    width: '100%',
    minWidth: 92,
    padding: '5px 6px',
    border: '1px solid',
    borderRadius: 6,
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  field: { marginBottom: 12 },
  fieldRow: { display: 'flex', gap: 10, alignItems: 'flex-end' }
};

export default PaymentsManager;
